"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Image from "next/image";

interface ApiSlide {
  id: number;
  image: string;
  text: string | null;
  textSize: number;
  duration: number;
}

interface Slide {
  id: number;
  image: string;
  text: string;
  textSize: number;
  durationMs: number;
}

const DEFAULT_highlightColor = "#2AABDE"; // fallback enquanto a API carrega (cor real vem do tb_website_config)
const TYPING_SPEED_MS = 55;

const TEXT_SIZE_MULTIPLIER: Record<number, number> = {
  1: 1,
  2: 1.4,
  3: 1.8,
  4: 2.4,
};

// Quebra o texto em caracteres pré-classificados (sem os colchetes literais)
// para que o efeito de digitação avance só nos caracteres visíveis.
function buildTypingQueue(text: string): { char: string; highlight: boolean }[] {
  const queue: { char: string; highlight: boolean }[] = [];
  let inside = false;
  for (const ch of text) {
    if (ch === "[") { inside = true; continue; }
    if (ch === "]") { inside = false; continue; }
    queue.push({ char: ch, highlight: inside });
  }
  return queue;
}

export default function HeroBanner() {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [typedCount, setTypedCount] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([]);
  const [highlightColor, setHighlightColor] = useState<string>(DEFAULT_highlightColor);

  // Carrega slides + cor primária do tenant
  useEffect(() => {
    const fetchData = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:5000";
        const [slidesRes, logosRes] = await Promise.all([
          fetch(`${backendUrl}/api/website/public/sliders`),
          fetch(`${backendUrl}/api/website/public/logos`),
        ]);
        if (slidesRes.ok) {
          const data = await slidesRes.json();
          if (data.success && Array.isArray(data.data)) {
            const apiSlides: Slide[] = (data.data as ApiSlide[]).map((s) => ({
              id: s.id,
              image: s.image,
              text: s.text || "",
              textSize: s.textSize >= 1 && s.textSize <= 4 ? s.textSize : 1,
              durationMs: (s.duration || 5) * 1000,
            }));
            setSlides(apiSlides);
            setImagesLoaded(new Array(apiSlides.length).fill(false));
          }
        }
        if (logosRes.ok) {
          const logos = await logosRes.json();
          if (logos?.data?.primary_color) setHighlightColor(logos.data.primary_color);
        }
      } catch (error) {
        console.error("Erro ao carregar dados do banner:", error);
      }
    };
    fetchData();
  }, []);

  // Fade-in inicial
  useEffect(() => {
    const t = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (slides.length === 0 ? 0 : (prev + 1) % slides.length));
  }, [slides.length]);

  const goToSlide = (index: number) => setCurrentSlide(index);

  // Auto-advance baseado na duração do slide atual
  useEffect(() => {
    if (slides.length === 0) return;
    const dur = slides[currentSlide]?.durationMs || 5000;
    const interval = setTimeout(nextSlide, dur);
    return () => clearTimeout(interval);
  }, [currentSlide, slides, nextSlide]);

  // Fila de caracteres pré-classificados (já sem os colchetes)
  const typingQueue = useMemo(
    () => buildTypingQueue(slides[currentSlide]?.text || ""),
    [slides, currentSlide]
  );

  // Reset typing ao trocar slide
  useEffect(() => {
    setTypedCount(0);
  }, [currentSlide, slides]);

  // Efeito de digitação: avança um caractere visível por intervalo
  useEffect(() => {
    if (typingQueue.length === 0) return;
    if (typedCount >= typingQueue.length) return;
    const t = setTimeout(() => setTypedCount((c) => c + 1), TYPING_SPEED_MS);
    return () => clearTimeout(t);
  }, [typedCount, typingQueue]);

  const handleImageLoad = (index: number) => {
    setImagesLoaded((prev) => {
      const n = [...prev];
      n[index] = true;
      return n;
    });
  };

  // Agrupa os caracteres já digitados em segmentos consecutivos por highlight
  const segments = useMemo(() => {
    const visible = typingQueue.slice(0, typedCount);
    const out: { text: string; highlight: boolean }[] = [];
    for (const item of visible) {
      const last = out[out.length - 1];
      if (last && last.highlight === item.highlight) last.text += item.char;
      else out.push({ text: item.char, highlight: item.highlight });
    }
    return out;
  }, [typingQueue, typedCount]);

  const currentTextSize = slides[currentSlide]?.textSize || 1;
  const highlightScale = TEXT_SIZE_MULTIPLIER[currentTextSize] || 1;
  const isTypingDone = typedCount >= typingQueue.length;

  if (slides.length === 0) {
    return (
      <div className="relative w-full h-[500px] md:h-[600px] lg:h-[820px] overflow-hidden bg-gray-900" />
    );
  }

  return (
    <div className="relative w-full h-[500px] md:h-[600px] lg:h-[820px] overflow-hidden bg-gray-900">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            currentSlide === index ? "opacity-100 z-10" : "opacity-0 z-0"
          } ${isLoaded ? "" : "opacity-0"}`}
        >
          <Image
            src={slide.image}
            alt={`Banner ${index + 1}`}
            fill
            priority={index === 0}
            className="object-cover"
            unoptimized
            onLoad={() => handleImageLoad(index)}
          />
        </div>
      ))}

      {/* Vignette */}
      <div
        className={`absolute inset-0 z-20 transition-opacity duration-1000 ${
          isLoaded ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background: `
            radial-gradient(
              ellipse 80% 80% at center,
              transparent 0%,
              transparent 40%,
              rgba(0, 0, 0, 0.2) 60%,
              rgba(0, 0, 0, 0.5) 80%,
              rgba(0, 0, 0, 0.8) 100%
            )
          `,
        }}
      />

      {/* Texto com efeito de digitação + destaque [ ] */}
      {slides[currentSlide]?.text && (
        <div className="absolute bottom-16 md:bottom-20 left-4 md:left-12 lg:left-20 z-25 max-w-[90%] md:max-w-[70%]">
          <div className="bg-black/70 backdrop-blur-md px-6 md:px-10 py-6 md:py-8 rounded-lg border-l-4" style={{ borderColor: highlightColor }}>
            <h1 className="text-white text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight whitespace-pre-line">
              {segments.map((seg, i) => (
                <span
                  key={i}
                  style={
                    seg.highlight
                      ? {
                          color: highlightColor,
                          fontSize: `${highlightScale}em`,
                          fontWeight: 900,
                          letterSpacing: "-0.02em",
                        }
                      : undefined
                  }
                >
                  {seg.text}
                </span>
              ))}
              {!isTypingDone && (
                <span className="animate-pulse" style={{ color: highlightColor }}>|</span>
              )}
            </h1>
          </div>
        </div>
      )}

      {/* Indicators */}
      <div
        className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 transition-all duration-1000 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            onClick={() => goToSlide(index)}
            className={`relative h-2 rounded-full transition-all duration-500 overflow-hidden ${
              currentSlide === index ? "w-10 bg-white" : "w-2 bg-white/50 hover:bg-white/70"
            }`}
            aria-label={`Ir para slide ${index + 1}`}
          >
            {currentSlide === index && (
              <span
                className="absolute inset-y-0 left-0 rounded-full"
                style={{
                  backgroundColor: highlightColor,
                  animation: `progress ${slide.durationMs}ms linear forwards`,
                }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Setas */}
      {slides.length > 1 && (
        <>
          <button
            onClick={() => setCurrentSlide((p) => (p - 1 + slides.length) % slides.length)}
            className={`absolute left-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 flex items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 transition-all duration-300 backdrop-blur-sm ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}
            aria-label="Slide anterior"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className={`absolute right-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 flex items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 transition-all duration-300 backdrop-blur-sm ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}
            aria-label="Próximo slide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      <style jsx>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}
